import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useOfferCount } from '../../contexts/OfferCountContext';
import { useContractCount } from '../../contexts/ContractCountContext';
import { useMessageCount } from '../../contexts/MessageCountContext';
import { useBugReporting } from '../../contexts/BugReportingContext';
import { setLastLoginRole } from '../../utils/lastLoginRole';
import { artistLogout } from '../../api/auth';
import AppDownloadNoticeModal from '../common/AppDownloadNoticeModal';
import { isArtistDemoActive, stopArtistDemo } from '../../utils/artistDemoRuntime';
import { resetSocket } from '../../lib/socket';

const SIDEBAR_SCROLL_KEY = 'artist-sidebar-scroll-top';

// 個人設定配下の詳細設定（宣材写真/経理情報/通知/パスワード）。プロフィール編集からも遷移可能。
const DETAIL_SETTINGS_ITEMS = [
  { to: '/artist/settings/artist-photography', label: '宣材写真設定' },
  { to: '/artist/settings/bank-info', label: '経理情報設定' },
  { to: '/artist/settings/notifications', label: '通知設定' },
  { to: '/artist/settings/change-password', label: 'パスワード変更' },
];

// クリックでページ遷移せず配下を開閉する折りたたみ見出し（管理者サイドバーと同じUI）。
function ArtistSidebarSubgroup({ label, iconClass, isOpen, onToggle, children }) {
  return (
    <li className="sidebar-item sidebar-subgroup">
      <button
        type="button"
        className={`sidebar-subgroup__toggle${isOpen ? ' is-open' : ''}`}
        onClick={(event) => {
          // 開閉のクリックがモバイルのサイドバー自動クローズに伝播しないようにする。
          event.stopPropagation();
          onToggle();
        }}
        aria-expanded={isOpen}
      >
        <span className="sidebar-item__content">
          <i className={`${iconClass} sidebar-item__icon`} aria-hidden="true" />
          <span>{label}</span>
        </span>
        <i className="fa-solid fa-chevron-down sidebar-subgroup__chevron" aria-hidden="true" />
      </button>
      {isOpen && <ul className="sidebar-subgroup__list">{children}</ul>}
    </li>
  );
}

function ArtistSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const restorePendingRef = useRef(true);
  const [isAppNoticeOpen, setIsAppNoticeOpen] = useState(false);
  const [detailSettingsOpen, setDetailSettingsOpen] = useState(() =>
    DETAIL_SETTINGS_ITEMS.some((item) => location.pathname === item.to)
  );
  const { offerCount } = useOfferCount();
  const { contractCount } = useContractCount();
  const {
    artistMessageCount,
    eventChatMessageCount,
    eventChatHasMention,
    groupChatHasMention,
  } = useMessageCount();
  const { enabled: bugReportingEnabled } = useBugReporting();

  const isActivePath = (path, { exact = false } = {}) => {
    if (!path) return false;
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleLogout = async () => {
    if (isArtistDemoActive()) {
      stopArtistDemo();
      resetSocket();
      navigate('/admin/workspaces/system/manuals/artist-demo');
      return;
    }
    try {
      const { ok, data } = await artistLogout();
      if (ok) {
        setLastLoginRole('artist');
        // ★ Header 即時非表示（アーティスト用）
        window.dispatchEvent(new CustomEvent('casticart:logout', { detail: { role: 'artist' } }));
        navigate(data.redirect && data.redirect !== '/' ? data.redirect : '/artist-login');
      }
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const isMobileViewport = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 700px)').matches;

  const closeMobileSidebar = () => {
    if (typeof document === 'undefined') return;
    document.body.classList.remove('sidebar-mobile-open');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('casticart:sidebar-visibility', { detail: { visible: false } })
      );
    }
  };

  const handleSidebarNav = (event) => {
    persistSidebarScroll();
    if (!isMobileViewport()) return;
    const interactive = event.target.closest('a, button, .sidebar-item.logout');
    if (interactive) closeMobileSidebar();
  };

  // 経理・報酬は今年 + tableビューをデフォルト
  const currentYear = new Date().getFullYear();
  const rewardsLinkTo = {
    pathname: '/artist/workspaces/finance/rewards',
    search: `?y=${currentYear}&mode=table`,
  };

  const SectionLabel = ({ iconClass, text }) => (
    <div className="sidebar-label">
      <i className={iconClass} aria-hidden="true" />
      <span>{text}</span>
    </div>
  );

  useEffect(() => {
    restorePendingRef.current = true;
  }, [location.key]);

  // 詳細設定配下のページに遷移したらサブメニューを自動で開く（手動で閉じても再遷移までは維持）。
  useEffect(() => {
    if (DETAIL_SETTINGS_ITEMS.some((item) => location.pathname === item.to)) {
      setDetailSettingsOpen(true);
    }
  }, [location.pathname]);

  const persistSidebarScroll = useCallback(() => {
    if (typeof window === 'undefined') return;
    const node = sidebarRef.current;
    if (!node) return;
    window.sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(node.scrollTop));
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (!restorePendingRef.current) return;
    const node = sidebarRef.current;
    if (!node) return;

    const saved = window.sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
    if (saved == null) {
      restorePendingRef.current = false;
      return;
    }

    const scrollTop = Number(saved);
    if (!Number.isFinite(scrollTop)) {
      restorePendingRef.current = false;
      return;
    }

    if (scrollTop > 0 && node.scrollHeight <= node.clientHeight) {
      return;
    }

    node.scrollTop = scrollTop;
    restorePendingRef.current = false;
  }, [bugReportingEnabled, location.key]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const node = sidebarRef.current;
    if (!node) return;

    const handleScroll = () => {
      persistSidebarScroll();
    };

    node.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      persistSidebarScroll();
      node.removeEventListener('scroll', handleScroll);
    };
  }, [persistSidebarScroll]);

  return (
    <div className="sidebar" onClick={handleSidebarNav} ref={sidebarRef}>
      <ul>
        <li className="sidebar-item">
          <Link
            to="/artist/workspaces/event-operations/dashboard"
            className={isActivePath('/artist/workspaces/event-operations/dashboard', { exact: true }) ? 'is-active' : ''}
          >
            <span className="sidebar-item__content">
              <i className="fa-solid fa-grip sidebar-item__icon" aria-hidden="true" />
              <span>ダッシュボード</span>
            </span>
          </Link>
        </li>
      </ul>

      {/* ★ イベント */}
      <SectionLabel iconClass="fa-solid fa-calendar" text="イベント" />
      <ul>
        <li className="sidebar-item">
          <Link
            to="/artist/workspaces/event-operations/events"
            className={isActivePath('/artist/workspaces/event-operations/events') ? 'is-active' : ''}
          >
            イベント一覧
          </Link>
        </li>
      </ul>

      {/* ★ メッセージ */}
      <SectionLabel iconClass="fa-solid fa-comment" text="メッセージ" />
      <ul>
        <li className="sidebar-item">
          <Link
            to="/artist/workspaces/communications/chat"
            className={isActivePath('/artist/workspaces/communications/chat', { exact: true }) ? 'is-active' : ''}
          >
            チャット
            {artistMessageCount > 0 && (
              <span className={`sidebar-count-badge ${groupChatHasMention ? 'sidebar-count-badge--mention' : 'sidebar-count-badge--alert'}`}>
                {artistMessageCount > 99 ? '99+' : artistMessageCount}
              </span>
            )}
          </Link>
        </li>
        <li className="sidebar-item">
          <Link
            to="/artist/workspaces/event-operations/event-chat"
            state={{ reset: true }}
            className={isActivePath('/artist/workspaces/event-operations/event-chat', { exact: true }) ? 'is-active' : ''}
          >
            イベントチャット
            {eventChatMessageCount > 0 && (
              <span className={`sidebar-count-badge ${eventChatHasMention ? 'sidebar-count-badge--mention' : 'sidebar-count-badge--alert'}`}>
                {eventChatMessageCount > 99 ? '99+' : eventChatMessageCount}
              </span>
            )}
          </Link>
        </li>
      </ul>

      {/* ★ 依頼関連 */}
      <SectionLabel iconClass="fa-solid fa-handshake" text="依頼関連" />
      <ul>
        <li className="sidebar-item">
          <Link
            to="/artist/workspaces/casting/offers"
            className={isActivePath('/artist/workspaces/casting/offers', { exact: true }) ? 'is-active' : ''}
          >
            オファー一覧
            {offerCount > 0 && (
              <span className="sidebar-count-badge sidebar-count-badge--alert">{offerCount}</span>
            )}
          </Link>
        </li>
        <li className="sidebar-item">
          <Link
            to="/artist/workspaces/contracts/dashboard"
            className={isActivePath('/artist/workspaces/contracts/dashboard', { exact: true }) ? 'is-active' : ''}
          >
            本契約一覧
            {contractCount > 0 && (
              <span className="sidebar-count-badge sidebar-count-badge--alert">{contractCount}</span>
            )}
          </Link>
        </li>
      </ul>

      {/* ★ サポート */}
      <SectionLabel iconClass="fa-solid fa-life-ring" text="サポート" />
      <ul>
        <li className="sidebar-item">
          <Link
            to="/artist/workspaces/concierge/dashboard"
            className={isActivePath('/artist/workspaces/concierge/dashboard', { exact: true }) ? 'is-active' : ''}
          >
            コンシェルジュ
          </Link>
        </li>
      </ul>

      {/* ★ 経理管理 */}
      <SectionLabel iconClass="fa-solid fa-money-check" text="経理管理" />
      <ul>
        <li className="sidebar-item">
          <Link
            to={rewardsLinkTo}
            className={isActivePath('/artist/workspaces/finance/rewards') ? 'is-active' : ''}
          >
            経理・報酬
          </Link>
        </li>
        <li className="sidebar-item">
          <Link
            to="/artist/workspaces/finance/invoice-maker"
            className={isActivePath('/artist/workspaces/finance/invoice-maker', { exact: true }) ? 'is-active' : ''}
          >
            請求書メーカー
          </Link>
        </li>
      </ul>

      {/* ★ 個人設定 */}
      <SectionLabel iconClass="fa-solid fa-gear" text="個人設定" />
      <ul>
        <li className="sidebar-item">
          <Link
            to="/artist/settings/profile"
            className={isActivePath('/artist/settings/profile', { exact: true }) ? 'is-active' : ''}
          >
            プロフィール
          </Link>
        </li>
        <ArtistSidebarSubgroup
          label="詳細設定"
          iconClass="fa-solid fa-sliders"
          isOpen={detailSettingsOpen}
          onToggle={() => setDetailSettingsOpen((prev) => !prev)}
        >
          {DETAIL_SETTINGS_ITEMS.map((item) => (
            <li className="sidebar-item" key={item.to}>
              <Link
                to={item.to}
                className={isActivePath(item.to, { exact: true }) ? 'is-active' : ''}
              >
                <span>{item.label}</span>
              </Link>
            </li>
          ))}
        </ArtistSidebarSubgroup>
        <li className="sidebar-item">
          <Link
            to="/artist/workspaces/support/manuals"
            className={isActivePath('/artist/workspaces/support/manuals', { exact: true }) ? 'is-active' : ''}
          >
            マニュアル
          </Link>
        </li>
        {bugReportingEnabled && (
          <li className="sidebar-item">
            <Link
              to="/artist/workspaces/support/bug-reports"
              className={isActivePath('/artist/workspaces/support/bug-reports', { exact: true }) ? 'is-active' : ''}
            >
              バグ報告
              <span className="sidebar-count-badge sidebar-count-badge--beta">BETA</span>
            </Link>
          </li>
        )}
      </ul>

      <div className="sidebar-bottom">
        <ul className="sidebar-bottom__actions">
          <li className="sidebar-item logout" onClick={handleLogout} style={{ cursor: 'pointer' }}>
            <span className="sidebar-item__content">
              <i className="fa-solid fa-right-from-bracket sidebar-item__icon" aria-hidden="true" />
              <span>ログアウト</span>
            </span>
          </li>
          <li className="sidebar-item sidebar-item--app-notice">
            <button
              type="button"
              className="sidebar-app-notice-button"
              onClick={(event) => {
                event.stopPropagation();
                setIsAppNoticeOpen(true);
              }}
            >
              <span className="sidebar-item__content">
                <i className="fa-solid fa-layer-group sidebar-item__icon" aria-hidden="true" />
                <span>アプリ版のお知らせ</span>
              </span>
            </button>
          </li>
        </ul>
        <div className="sidebar-legal">
          <Link to="/terms" target="_blank" rel="noopener noreferrer" className="sidebar-legal__link">
            利用規約
          </Link>
          <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="sidebar-legal__link">
            プライバシーポリシー
          </Link>
        </div>
      </div>
      <AppDownloadNoticeModal open={isAppNoticeOpen} onClose={() => setIsAppNoticeOpen(false)} />
    </div>
  );
}

export default ArtistSidebar;
